/**
 * /pulse — Landing directory of curated Neighborhood Pulse localities.
 * Users pick a city → routed to /pulse/{slug}.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { MapPin, Users, Activity, Leaf } from "lucide-react";
import PulseItemsFeed from "@/components/PulseItemsFeed";

export default function NeighborhoodPulse() {
  const [items, setItems] = useState(null);
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const cityHint = (searchParams.get("city") || "").trim().toLowerCase();

  useEffect(() => {
    http.get("/neighborhood").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);

  // If arriving from a location article with `?city=<name>`, try to jump
  // straight to the matching neighborhood.  Otherwise the user still sees
  // the filtered directory (see filter logic below).
  useEffect(() => {
    if (!cityHint || !items || items.length === 0) return;
    const match = items.find(
      (n) => (n.city || "").toLowerCase() === cityHint || (n.slug || "").toLowerCase() === cityHint,
    );
    if (match) nav(`/pulse/${match.slug}`, { replace: true });
  }, [cityHint, items, nav]);

  const filtered = cityHint && items
    ? items.filter((n) => (n.city || "").toLowerCase().includes(cityHint) || (n.slug || "").toLowerCase().includes(cityHint))
    : items;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="pulse-landing">
      <div className="overline">SOCIALPULSE · NEIGHBORHOOD PULSE</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-2">A daily check-in with your city.</h1>
      <p className="mt-3 max-w-2xl" style={{ color: "var(--wofy-text-soft)" }}>
        Every neighbourhood has a pulse — potholes worth fixing, wildlife worth noticing, and neighbours worth turning up for.
        Pick your city to see today's civic reports, nature observations, and a friendly nudge to act.
      </p>

      {items === null ? (
        <p className="mt-10 text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading cities…</p>
      ) : (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pulse-grid">
          {(filtered || []).map((n) => <LocalityCard key={n.slug} n={n} />)}
        </div>
      )}

      {cityHint && filtered && filtered.length === 0 && (
        <div className="mt-6 p-4 rounded-xl border" style={{ borderColor: "var(--wofy-line)", background: "#FDF4FF" }}>
          <p className="text-sm">
            No Neighborhood Pulse locality for <strong>"{searchParams.get("city")}"</strong> yet —
            we're rolling out one at a time. <Link to="/contact" className="underline">Request it →</Link>
          </p>
        </div>
      )}

      <p className="mt-12 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
        Don't see your city? We're rolling out one locality at a time. <Link to="/contact" className="underline">Ask us to add yours →</Link>
      </p>

      <PulseItemsFeed canAdd={true} />
    </div>
  );
}

function LocalityCard({ n }) {
  const path = `/pulse/${n.slug}`;
  const admin = [n.city, n.district, n.state, n.country].filter(Boolean).join(" · ");
  const a = n.activity_last_7d || {};
  return (
    <Link
      to={path}
      data-testid={`pulse-card-${n.slug.replace(/\//g, "-")}`}
      className="group rounded-xl overflow-hidden border bg-white flex flex-col hover:shadow-lg transition-shadow"
      style={{ borderColor: "var(--wofy-border)" }}
    >
      <div className="aspect-[16/9] overflow-hidden">
        <img src={n.banner_image} alt={n.city} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: n.accent || "var(--wofy-primary)" }}>
          {admin}
        </div>
        <h3 className="font-display text-xl mt-0.5">{n.city}</h3>
        <p className="mt-2 text-xs line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>
          {n.banner_headline}
        </p>
        <div className="mt-3 pt-3 border-t flex items-center gap-4 text-[11px] font-mono" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
          <span title="Population" className="flex items-center gap-1"><Users className="w-3 h-3" /> {formatPop(n.population)}</span>
          <span title="Civic reports (7d)" className="flex items-center gap-1"><Activity className="w-3 h-3" /> {a.civic_reports || 0}</span>
          <span title="Nature obs (7d)" className="flex items-center gap-1"><Leaf className="w-3 h-3" /> {a.observations || 0}</span>
        </div>
      </div>
    </Link>
  );
}

function formatPop(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}
